// src/routes/index.js
import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AppContext';

// Pages - Públicas
import HomePage from '../pages/Home';
import CoursesPage from '../pages/Courses';
import CourseDetailPage from '../pages/CourseDetail';
import CourseViewPage from '../pages/CourseView';
import VerificarCertificadoPage from '../pages/VerificarCertificado';
import MisCertificadosPage from '../pages/MisCertificados';

// Pages - Autenticación
import { LoginPage, RegisterPage } from '../pages/Auth';

// Pages - Estudiante
import Dashboard from '../pages/Dashboard';

// Pages - Admin
import AdminDashboard from '../pages/AdminDashboard';
import AdminAccesosPage from '../pages/AdminAccesos';
import AdminCertificadosPage from '../pages/AdminCertificados';
import RedeprogAdminPage from '../pages/RedeprogAdminPage';

// Pages - Instructor
import InstructorDashboard from '../pages/InstructorDashboard';
import GradingPage from '../pages/GradingPage';
import StudentsPage from '../pages/StudentsPage';
import ChatPage from '../pages/ChatPage';
import ForumPage from '../pages/ForumPage';

// ============ SESIONES (online/grabadas) ============
import SesionesPage from '../pages/SesionesPage';
import SesionVivaPage from '../pages/SesionVivaPage';
// ============ FIN SESIONES ============

// ============ INSCRIPCIÓN INSTITUCIÓN ============
import InscripcionInstitucionPage from '../pages/InscripcionInstitucionPage';
import MiInstitucionPage from '../pages/MiInstitucionPage';
// ============ FIN INSCRIPCIÓN ============

// ============ PENSIONES / PAGOS ============
import MisPensionesPage from '../pages/MisPensionesPage';
import PagoPensionResultadoPage from '../pages/PagoPensionResultadoPage';
// ============ FIN PENSIONES ============

// Protected Route (cualquier usuario autenticado)
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

// Admin Only Route
function AdminRoute() {
  const { isAuthenticated, loading, user, isFenagroSuperAdmin } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // SuperAdmin de Fenagro también entra -- mismo criterio ya usado en
  // el backend (RequireEducaTGlobalAdminFilter) y en el registro de
  // instituciones. Sin esto, alguien SuperAdmin no tenía forma de
  // llegar al panel de EducaT sin que otro admin ya existente le
  // asignara el rol Administrador propio de EducaT primero.
  if (user?.role !== 'admin' && !isFenagroSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

// Fase 6 Redeprog SaaS: guard para el SuperAdmin de Redeprog.
// Espeja RequireSuperAdminRedeprogFilter del backend.
function SuperAdminRedeprogRoute() {
  const { isAuthenticated, loading, isSuperAdminRedeprog } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdminRedeprog()) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// Instructor Only Route
function InstructorRoute() {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'instructor' && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

// Student Only Route
function StudentRoute() {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === 'instructor') {
    return <Navigate to="/instructor" replace />;
  }
  return <Outlet />;
}

// Public only (redirect if logged in)
function PublicOnlyRoute() {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return null;
  if (isAuthenticated) {
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'instructor') {
      return <Navigate to="/instructor" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ========== Public Routes ========== */}
      <Route path="/" element={<HomePage />} />
      <Route path="/courses" element={<CoursesPage />} />
      <Route path="/course/:slug" element={<CourseDetailPage />} />
      <Route path="/instituciones/inscripcion" element={<InscripcionInstitucionPage />} />
      <Route path="/cert/:codigo" element={<VerificarCertificadoPage />} />

      {/* ========== Auth Routes ========== */}
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      {/* ========== Admin Routes ========== */}
      <Route element={<AdminRoute />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/students" element={<AdminDashboard />} />
        <Route path="/admin/courses" element={<AdminDashboard />} />
        <Route path="/admin/grading" element={<AdminDashboard />} />
        <Route path="/admin/instructors" element={<AdminDashboard />} />
        <Route path="/admin/accesos" element={<AdminAccesosPage />} />
        <Route path="/admin/certificados" element={<AdminCertificadosPage />} />
      </Route>

      {/* ========== Redeprog SuperAdmin Routes (Fase 6) ========== */}
      <Route element={<SuperAdminRedeprogRoute />}>
        <Route path="/redeprog/organizaciones" element={<RedeprogAdminPage />} />
      </Route>

      {/* ========== Instructor Routes ========== */}
      <Route element={<InstructorRoute />}>
        <Route path="/instructor" element={<InstructorDashboard />} />
        <Route path="/instructor/courses" element={<InstructorDashboard />} />
        <Route path="/instructor/course/:courseId" element={<InstructorDashboard />} />
        <Route path="/instructor/grading" element={<GradingPage />} />
        <Route path="/instructor/grading/:submissionId" element={<GradingPage />} />
        <Route path="/instructor/students" element={<StudentsPage />} />
        <Route path="/instructor/chat" element={<ChatPage />} />
        <Route path="/instructor/forums" element={<ForumPage />} />
        <Route path="/instructor/forums/:courseId" element={<ForumPage />} />
        <Route path="/instructor/forums/:courseId/:topicId" element={<ForumPage />} />
        <Route path="/instructor/reports" element={<InstructorDashboard />} />
      </Route>

      {/* ========== Student Protected Routes ========== */}
      <Route element={<StudentRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/learn/:slug" element={<CourseViewPage />} />
        <Route path="/profile" element={<Dashboard />} />
        <Route path="/my-courses" element={<Dashboard />} />
        <Route path="/mis-certificados" element={<MisCertificadosPage />} />
      </Route>

      {/* ========== Shared Routes (authenticated) ========== */}
      <Route element={<ProtectedRoute />}>
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/forums" element={<ForumPage />} />
        <Route path="/forums/:courseId" element={<ForumPage />} />
        <Route path="/forums/:courseId/:topicId" element={<ForumPage />} />
        {/* ============ SESIONES ROUTES ============ */}
        <Route path="/sesiones" element={<SesionesPage />} />
        <Route path="/sesiones/:id/vivo" element={<SesionVivaPage />} />
        {/* ============ FIN SESIONES ============ */}
        {/* ============ ALIANZAS EDUCATIVAS ============ */}
        <Route path="/mi-institucion" element={<MiInstitucionPage />} />
        {/* ============ FIN ALIANZAS EDUCATIVAS ============ */}
        {/* ============ PENSIONES / PAGOS ============ */}
        <Route path="/mi-institucion/pensiones" element={<MisPensionesPage />} />
        <Route path="/mi-institucion/pensiones/resultado" element={<PagoPensionResultadoPage />} />
        {/* ============ FIN PENSIONES ============ */}
      </Route>

      {/* ========== 404 ========== */}
      <Route
        path="*"
        element={
          <div style={{
            padding: '100px',
            textAlign: 'center',
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <h1 style={{ fontSize: '6rem', marginBottom: '1rem', opacity: 0.5 }}>404</h1>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>Página no encontrada</p>
            <a
              href="/"
              style={{
                padding: '12px 24px',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
              }}
            >
              Volver al inicio
            </a>
          </div>
        }
      />
    </Routes>
  );
}

export default AppRoutes;

